/*
 * Greenphones – sold-out variant landing (assets/gp-variant-landing.js)
 * -------------------------------------------------------------------
 * When a product page is LOADED with ?variant=<id> and that variant is sold out,
 * select the nearest in-stock variant of the same product, show a notice near the
 * price with a "Vis likevel" link back to the requested variant, and update the URL
 * (history.replaceState via Focal's own selectVariant()).
 *
 * Never runs when: no ?variant= in the URL, ?keep=1 is present, the requested variant
 * is in stock or unknown, no variant of the product is in stock, or the customer has
 * already changed the selection. Only the main product form (product-variants[update-url])
 * is touched. Sold-out variants stay visible/selectable (Varsle meg keeps working).
 *
 * Scoring ("nearest"):
 *   Tier 1  same storage + same colour (other grade)  -> closest grade
 *   Tier 2  same storage                              -> closest grade, then closest price
 *   Tier 3  same colour                               -> closest storage, closest grade, closest price
 *   Tier 4  anything in stock                         -> cheapest
 *   Grade proximity uses the order Premium > Grade A > Grade B > Grade C (distance = steps);
 *   on a tie the better grade wins, then the closer price, then the lower price.
 * SEO: canonical + JSON-LD are rendered server-side for the requested variant and are untouched.
 */
(function () {
  'use strict';
  if (window.__gpVariantLanding) return;
  window.__gpVariantLanding = true;

  var params = new URLSearchParams(window.location.search);
  var requestedId = parseInt(params.get('variant'), 10);
  if (!requestedId || params.get('keep') === '1') return;

  var GRADE_ORDER = ['premium', 'grade a', 'grade b', 'grade c'];
  var RE_STORAGE = /lagring|kapasitet|storage|minne|gb/i;
  var RE_COLOUR = /farge|colou?r/i;
  var RE_GRADE = /tilstand|grade|stand|condition/i;

  function gradeRank(value) {
    var v = String(value || '').toLowerCase().trim();
    var i = GRADE_ORDER.indexOf(v);
    if (i > -1) return i;
    if (/premium|som ny/.test(v)) return 0;
    var m = v.match(/\b(?:grade\s*)?([abc])\b/);
    return m ? GRADE_ORDER.indexOf('grade ' + m[1]) : -1;
  }
  function gradeDistance(a, b) {
    if (a === b) return 0;
    var ra = gradeRank(a), rb = gradeRank(b);
    return (ra < 0 || rb < 0) ? 1.5 : Math.abs(ra - rb);
  }
  function storageGB(value) {
    var m = String(value || '').replace(/\s/g, '').match(/(\d+(?:[.,]\d+)?)(TB|GB)/i);
    if (!m) return null;
    var n = parseFloat(m[1].replace(',', '.'));
    return /tb/i.test(m[2]) ? n * 1024 : n;
  }
  function storageDistance(a, b) {
    if (a === b) return 0;
    var ga = storageGB(a), gb = storageGB(b);
    return (ga === null || gb === null) ? 99 : Math.abs(Math.log2(ga) - Math.log2(gb));
  }

  function optionIndexes(product) {
    var idx = { storage: -1, colour: -1, grade: -1 };
    (product.options || []).forEach(function (opt, i) {
      var name = typeof opt === 'string' ? opt : opt.name;
      if (idx.grade < 0 && RE_GRADE.test(name)) idx.grade = i;
      else if (idx.colour < 0 && RE_COLOUR.test(name)) idx.colour = i;
      else if (idx.storage < 0 && RE_STORAGE.test(name)) idx.storage = i;
    });
    return idx;
  }

  function pickNearest(product, requested) {
    var idx = optionIndexes(product);
    var opt = function (v, i) { return i < 0 ? null : v.options[i]; };
    var rS = opt(requested, idx.storage), rC = opt(requested, idx.colour), rG = opt(requested, idx.grade);
    var candidates = product.variants.filter(function (v) { return v.available && v.id !== requested.id; });
    if (!candidates.length) return null;

    var scored = candidates.map(function (v) {
      var sameS = idx.storage < 0 || opt(v, idx.storage) === rS;
      var sameC = idx.colour < 0 || opt(v, idx.colour) === rC;
      // Any other non-grade option must match for tier 1 (e.g. a 4th option)
      var othersSame = v.options.every(function (val, i) {
        return i === idx.grade || i === idx.storage || i === idx.colour || val === requested.options[i];
      });
      var tier = (sameS && sameC && othersSame) ? 1 : (idx.storage >= 0 && sameS) ? 2 : (idx.colour >= 0 && sameC) ? 3 : 4;
      var g = idx.grade < 0 ? 0 : gradeDistance(opt(v, idx.grade), rG);
      var gRank = idx.grade < 0 ? 0 : gradeRank(opt(v, idx.grade));
      var s = idx.storage < 0 ? 0 : storageDistance(opt(v, idx.storage), rS);
      var p = Math.abs(v.price - requested.price);
      var key;
      if (tier === 1) key = [g, gRank < 0 ? 9 : gRank, p, v.price];
      else if (tier === 2) key = [g, gRank < 0 ? 9 : gRank, p, v.price];
      else if (tier === 3) key = [s, g, gRank < 0 ? 9 : gRank, p, v.price];
      else key = [v.price, g, s];
      return { v: v, key: [tier].concat(key) };
    });
    scored.sort(function (a, b) {
      for (var i = 0; i < a.key.length; i++) { if (a.key[i] !== b.key[i]) return a.key[i] - b.key[i]; }
      return 0;
    });
    return { variant: scored[0].v, tier: scored[0].key[0], idx: idx };
  }

  function label(variant, idx) {
    var parts = [];
    [idx.colour, idx.storage, idx.grade].forEach(function (i) { if (i > -1) parts.push(variant.options[i]); });
    variant.options.forEach(function (val, i) { if (i !== idx.colour && i !== idx.storage && i !== idx.grade) parts.push(val); });
    return parts.join(' \u00b7 ');
  }

  function findNotice(pv) {
    var scope = pv.closest('product-rerender, .product, section') || document;
    return scope.querySelector('[data-gp-soldout-notice]') || document.querySelector('[data-gp-soldout-notice]');
  }

  function run(pv) {
    var product = pv.product;
    var current = pv.selectedVariant;
    var requested = product.variants.find(function (v) { return v.id === requestedId; });
    if (!requested || requested.available) return;              // unknown or in stock -> nothing to do
    if (!current || current.id !== requested.id) return;        // customer already changed selection
    var result = pickNearest(product, requested);
    if (!result) return;                                        // nothing in stock at all -> leave as is

    var best = result.variant;
    var notice = findNotice(pv);
    var form = pv.masterSelector && pv.masterSelector.form;
    var autoSelectedId = best.id;
    pv.selectVariant(best.id);                                  // Focal: replaceState + variant:changed + disabled states
    if (!pv.selectedVariant || pv.selectedVariant.id !== best.id) return;

    if (notice) {
      var keepUrl = new URL(window.location.href);
      keepUrl.searchParams.set('variant', String(requested.id));
      keepUrl.searchParams.set('keep', '1');
      notice.innerHTML = '';
      var text = document.createElement('span');
      text.className = 'gp-soldout-notice__text';
      text.textContent = 'Varianten du valgte (' + label(requested, result.idx) + ') er utsolgt \u2013 vi har valgt n\u00e6rmeste tilgjengelige. ';
      var link = document.createElement('a');
      link.className = 'gp-soldout-notice__link link';
      link.href = keepUrl.pathname + keepUrl.search + keepUrl.hash;
      link.textContent = 'Vis likevel';
      link.setAttribute('data-gp-show-requested', '');
      link.addEventListener('click', function (e) {
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return; // new tab -> plain link with keep=1
        e.preventDefault();
        autoSelectedId = null;
        notice.hidden = true;
        pv.selectVariant(requested.id);
        try {
          var u = new URL(window.location.href);
          u.searchParams.set('variant', String(requested.id));
          u.searchParams.set('keep', '1');
          history.replaceState({ path: u.toString() }, '', u.toString());
        } catch (err) { /* ignore */ }
      });
      notice.appendChild(text);
      notice.appendChild(link);
      notice.hidden = false;
    }

    if (form) {
      form.addEventListener('variant:changed', function (event) {
        var v = event.detail && event.detail.variant;
        if (autoSelectedId && v && v.id !== autoSelectedId && notice) {
          notice.hidden = true;
          autoSelectedId = null;
        }
      });
    }

    try {
      (window.dataLayer = window.dataLayer || []).push({
        event: 'gp_soldout_variant_redirect',
        gp_requested_variant: String(requested.id),
        gp_selected_variant: String(best.id),
        gp_match_tier: result.tier
      });
    } catch (err) { /* ignore */ }
  }

  function init() {
    var tries = 0;
    (function wait() {
      var pv = document.querySelector('product-variants[update-url]');
      if (pv && pv.product && pv.masterSelector && typeof pv.selectVariant === 'function') {
        try { run(pv); } catch (err) { if (window.console) console.warn('[gp-variant-landing]', err); }
        return;
      }
      if (++tries < 200) setTimeout(wait, 50);                  // up to ~10 s for theme.js + product JSON
    })();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
